import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppError } from "@/jenesys-types/errors";
import { Paginated } from "@/jenesys-types/data/common";
import { ListQuerySchema } from "@/jenesys-types/data/communication";

interface DocumentSearchState {
  isLoading: boolean;
  error?: AppError;
  results: Paginated<ListQuerySchema> | null;
  query: string;
}

const initialState: DocumentSearchState = {
  isLoading: false,
  error: undefined,
  results: null,
  query: ""
};

export interface SearchDocumentsPayload {
  companyId: string;
  query: string;
  limit?: number;
  offset?: number;
}

const documentSearchSlice = createSlice({
  name: "documentSearch",
  initialState,
  reducers: {
    searchDocuments: {
      reducer: (state: DocumentSearchState, action: PayloadAction<SearchDocumentsPayload>) => {
        state.isLoading = true;
        state.error = undefined;
        state.query = action.payload.query;
      },
      prepare: (companyId: string, query: string, limit = 10, offset = 0) => {
        const payload = { companyId, query, limit, offset };
        return { payload };
      }
    },
    searchDocumentsSuccess: {
      reducer: (state: DocumentSearchState, action: PayloadAction<Paginated<ListQuerySchema>>) => {
        state.isLoading = false;
        state.results = action.payload;
        state.error = undefined;
      },
      prepare: (results: Paginated<ListQuerySchema>) => ({
        payload: results
      })
    },
    searchDocumentsFailure: {
      reducer: (state: DocumentSearchState, action: PayloadAction<AppError>) => {
        state.isLoading = false;
        state.error = action.payload;
      },
      prepare: (error: AppError) => ({
        payload: error
      })
    },
    clearSearchResults: (state: DocumentSearchState) => {
      state.results = null;
      state.query = "";
      state.error = undefined;
    }
  }
});

export const {
  searchDocuments,
  searchDocumentsSuccess,
  searchDocumentsFailure,
  clearSearchResults
} = documentSearchSlice.actions;

export const selectDocumentSearchState = (state: any) => state.features.transactions.documentSearch;
export const selectDocumentSearchResults = (state: any) => selectDocumentSearchState(state).results;
export const selectDocumentSearchLoading = (state: any) => selectDocumentSearchState(state).isLoading;
export const selectDocumentSearchError = (state: any) => selectDocumentSearchState(state).error;

export default documentSearchSlice.reducer;